import { useCallback, useState } from "react";
import { CombinedGraphQLErrors, ServerError } from "@apollo/client/errors";
import { useApolloClient, useMutation } from "@apollo/client/react";
import {
  CREATE_EVENT,
  GET_EVENT_AGREEMENT,
  LIST_RECOVERABLE_EVENTS,
  SIGN_EVENT_AGREEMENT,
  UPSERT_EVENT_LANDING_PAGE,
} from "@app/graphql/events.queries";
import { INITIALIZE_EVENT_AIR_CONTRACTS } from "@app/graphql/policies.queries";
import { useOrganizationId } from "@app/hooks/use-organization-id";
import { GraphQLRequestTimeoutError } from "@app/lib/apollo-client";
import { createSlug } from "@app/lib/events-admin-utils";
import { encodeEventDescription } from "@app/lib/event-description";
import {
  MINIMUM_TRAVELER_COUNT,
  type PendingEventFormValues,
  clearPendingEvent,
  loadPendingEvent,
  savePendingEvent,
} from "@app/lib/pending-event-storage";
import { useNotifications } from "@app/lib/ui-foundation";
import { useJwtAuth } from "@app/providers/jwt-auth-provider";
import { useNavigate } from "react-router";

const CREATE_RECOVERY_WINDOW_MS = 5 * 60 * 1000;

type RecoverableEvent = {
  eventId: string;
  eventName: string;
  destination: string;
  startDate: string;
  endDate: string;
  status: string;
  createdAt: string;
};

type EventAgreement = {
  signedContractId: string | null;
  status: "PREPARED" | "SIGNING" | "SIGNED" | "REJECTED" | "UNKNOWN";
};

export type AgreementSubmissionPreview = {
  contractId: string;
  content: string;
  values: Record<string, string>;
  sourceValues: string;
  acceptedAt: string;
  renderVersion: string;
  previewHash: string;
};

export type AgreementSubmissionPhase =
  | "idle"
  | "creating"
  | "configuring"
  | "signing";

type AgreementOutcome =
  | { action: "complete" }
  | { action: "sign"; message: string }
  | { action: "retry"; message: string }
  | { action: "block"; message: string };

type UseAgreementSubmissionOptions = {
  formValues: PendingEventFormValues | null;
  contractId: string | null;
  agreementPreview: AgreementSubmissionPreview | null;
  signatureName: string;
  canSubmit: boolean;
};

function getAgreementOutcome(
  agreement: EventAgreement | null,
): AgreementOutcome {
  if (!agreement) {
    return {
      action: "sign",
      message: "Agreement signing needs support review before it can continue.",
    };
  }

  const status = agreement.status;
  switch (status) {
    case "PREPARED":
      return {
        action: "sign",
        message:
          "Agreement signing needs support review before it can continue.",
      };
    case "SIGNING":
      return {
        action: "block",
        message:
          "Agreement signing is still being processed. Please try again later.",
      };
    case "SIGNED":
      return agreement.signedContractId
        ? { action: "complete" }
        : {
            action: "block",
            message:
              "Agreement signing needs support review before it can continue.",
          };
    case "REJECTED":
      return {
        action: "retry",
        message:
          "The agreement details were rejected. Review the metadata and preview it again.",
      };
    case "UNKNOWN":
      return {
        action: "block",
        message:
          "Agreement signing needs support review before it can continue.",
      };
    default: {
      const exhaustiveStatus: never = status;
      return exhaustiveStatus;
    }
  }
}

function isPossiblyCommittedCreateError(error: unknown) {
  return (
    (ServerError.is(error) && error.statusCode >= 500) ||
    (CombinedGraphQLErrors.is(error) &&
      error.errors.some(
        ({ message }) =>
          message.trim().toLowerCase() === "execution timed out.",
      )) ||
    error instanceof GraphQLRequestTimeoutError
  );
}

export function useAgreementSubmission({
  formValues,
  contractId,
  agreementPreview,
  signatureName,
  canSubmit,
}: UseAgreementSubmissionOptions) {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const organizationId = useOrganizationId();
  const { dismissAll, showError } = useNotifications();
  const { user } = useJwtAuth();
  const [submissionPhase, setSubmissionPhase] =
    useState<AgreementSubmissionPhase>("idle");
  const [createEventMutation] = useMutation<{
    createEvent: { eventId: string } | null;
  }>(CREATE_EVENT);
  const [upsertEventLandingPageMutation] = useMutation(
    UPSERT_EVENT_LANDING_PAGE,
  );
  const [signEventAgreementMutation] = useMutation<{
    signEventAgreement: EventAgreement;
  }>(SIGN_EVENT_AGREEMENT);
  const [initializeEventAirContractsMutation] = useMutation<{
    initializeEventAirContracts: { eventId: string } | null;
  }>(INITIALIZE_EVENT_AIR_CONTRACTS);

  const submitAgreement = useCallback(async () => {
    const currentPreview = agreementPreview;
    if (
      !formValues ||
      !canSubmit ||
      !contractId ||
      !currentPreview ||
      !user?.email ||
      !user.name
    )
      return;
    dismissAll();

    const travelerCount = formValues.passengers.trim();
    if (
      !/^\d+$/.test(travelerCount) ||
      Number(travelerCount) < MINIMUM_TRAVELER_COUNT
    ) {
      showError(
        "Traveler count needs attention",
        `Enter at least ${MINIMUM_TRAVELER_COUNT} travelers before submitting.`,
        0,
      );
      navigate("/", { replace: true });
      return;
    }

    setSubmissionPhase("creating");

    try {
      if (!organizationId) {
        throw new Error("organizationId not available");
      }

      const persistedWorkflow = loadPendingEvent()?.agreementWorkflow;
      let workflow =
        persistedWorkflow?.templateContractId === contractId
          ? persistedWorkflow
          : formValues.agreementWorkflow?.templateContractId === contractId
            ? formValues.agreementWorkflow
            : undefined;
      const isResumingSigning = workflow?.step === "SIGNING";
      const hasPersistedSigningEvidence =
        isResumingSigning &&
        Boolean(workflow?.typedSignature) &&
        Boolean(workflow?.previewHash) &&
        Boolean(workflow?.previewAcceptedAt) &&
        Boolean(workflow?.previewRenderVersion);
      if (
        hasPersistedSigningEvidence &&
        workflow?.typedSignature !== signatureName
      ) {
        throw new Error(
          "The signature changed after signing began. Restore the original signature and try again.",
        );
      }
      const startDate = new Date(formValues.departDate).toISOString();
      const endDate = new Date(formValues.returnDate).toISOString();
      let eventId = workflow?.eventId;

      if (!eventId) {
        const createAttemptedAt = Date.now();
        try {
          const { data } = await createEventMutation({
            variables: {
              organizerName: user.name.trim(),
              input: {
                organizationId,
                eventName: formValues.eventName,
                eventType: formValues.eventType,
                destination: formValues.destinationLabel,
                primaryAirportCode: formValues.primaryAirportCode,
                travelType: "FLIGHT_REQUIRED",
                venue: formValues.venueName,
                venueAddress: formValues.venueAddress,
                ...(formValues.description
                  ? {
                      description: encodeEventDescription(
                        formValues.description,
                      ),
                    }
                  : {}),
                startDate,
                endDate,
                eventTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                visibility: "PRIVATE",
                origin: "STANDALONE",
                contractId,
                estimatedTravelerCount: Number(travelerCount),
              },
            },
          });
          eventId = data?.createEvent?.eventId;
        } catch (error) {
          if (!isPossiblyCommittedCreateError(error)) throw error;

          const { data } = await apolloClient.query<{
            listEvents: RecoverableEvent[];
          }>({
            query: LIST_RECOVERABLE_EVENTS,
            variables: {
              organizationId,
              organizerUserId: user.id,
              q: formValues.eventName,
              limit: 10,
            },
            fetchPolicy: "network-only",
          });
          eventId = (data?.listEvents ?? []).find((event) => {
            const createdAt = Date.parse(event.createdAt);
            const status = event.status.trim().toUpperCase();
            return (
              event.eventName === formValues.eventName &&
              event.destination === formValues.destinationLabel &&
              event.startDate === startDate &&
              event.endDate === endDate &&
              ["DRAFT", "REVIEW", "IN_REVIEW"].includes(status) &&
              Number.isFinite(createdAt) &&
              createdAt >= createAttemptedAt - CREATE_RECOVERY_WINDOW_MS &&
              createdAt <= Date.now() + 60_000
            );
          })?.eventId;
          if (!eventId) throw error;
        }
      }

      if (!eventId) {
        throw new Error("Unable to create your event. Please try again.");
      }

      workflow = {
        eventId,
        templateContractId: contractId,
        requestId: workflow?.requestId ?? crypto.randomUUID(),
        step: workflow?.step ?? "CONFIGURING",
        values: hasPersistedSigningEvidence
          ? (workflow?.values ?? currentPreview.values)
          : currentPreview.values,
        typedSignature: hasPersistedSigningEvidence
          ? (workflow?.typedSignature ?? signatureName)
          : signatureName,
        previewHash: hasPersistedSigningEvidence
          ? (workflow?.previewHash ?? currentPreview.previewHash)
          : currentPreview.previewHash,
        previewAcceptedAt: hasPersistedSigningEvidence
          ? (workflow?.previewAcceptedAt ?? currentPreview.acceptedAt)
          : currentPreview.acceptedAt,
        previewRenderVersion: hasPersistedSigningEvidence
          ? (workflow?.previewRenderVersion ?? currentPreview.renderVersion)
          : currentPreview.renderVersion,
      };
      savePendingEvent({ ...formValues, agreementWorkflow: workflow });

      if (workflow.step === "CONFIGURING") {
        setSubmissionPhase("configuring");
        const { data: airContractsData } =
          await initializeEventAirContractsMutation({
            variables: { eventId, organizationId },
          });

        if (!airContractsData?.initializeEventAirContracts?.eventId) {
          throw new Error(
            "Unable to initialize authorized airlines for this event. Please try again.",
          );
        }

        const landingPageSlug = createSlug(formValues.eventName);
        if (!landingPageSlug) {
          throw new Error("Unable to generate a landing page slug.");
        }

        await upsertEventLandingPageMutation({
          variables: {
            eventId,
            input: {
              slug: landingPageSlug,
              title: formValues.eventName,
              subtitle: "",
              description: encodeEventDescription(formValues.description),
              registrationOpen: false,
            },
          },
        });
        workflow = { ...workflow, step: "SIGNING" };
        savePendingEvent({ ...formValues, agreementWorkflow: workflow });
      }

      setSubmissionPhase("signing");
      if (isResumingSigning) {
        const { data } = await apolloClient.query<{
          getEventAgreement: EventAgreement | null;
        }>({
          query: GET_EVENT_AGREEMENT,
          variables: { eventId, organizationId },
          fetchPolicy: "network-only",
        });
        const existingOutcome = getAgreementOutcome(
          data?.getEventAgreement ?? null,
        );
        if (existingOutcome.action === "complete") {
          clearPendingEvent();
          navigate("/event-submitted", { replace: true });
          return;
        }
        if (existingOutcome.action === "block") {
          throw new Error(existingOutcome.message);
        }
        if (existingOutcome.action === "retry") {
          workflow = {
            ...workflow,
            requestId: crypto.randomUUID(),
            values: currentPreview.values,
            typedSignature: signatureName,
            previewHash: currentPreview.previewHash,
            previewAcceptedAt: currentPreview.acceptedAt,
            previewRenderVersion: currentPreview.renderVersion,
          };
          savePendingEvent({ ...formValues, agreementWorkflow: workflow });
        }
      }

      if (
        !workflow.typedSignature ||
        !workflow.previewHash ||
        !workflow.previewAcceptedAt ||
        !workflow.previewRenderVersion
      ) {
        throw new Error("Agreement preview is no longer current.");
      }

      let agreement: EventAgreement | null = null;
      try {
        const { data } = await signEventAgreementMutation({
          variables: {
            eventId,
            organizationId,
            requestId: workflow.requestId,
            typedSignature: workflow.typedSignature,
            previewHash: workflow.previewHash,
            previewAcceptedAt: workflow.previewAcceptedAt,
            previewRenderVersion: workflow.previewRenderVersion,
            values: JSON.stringify(workflow.values),
          },
        });
        agreement = data?.signEventAgreement ?? null;
      } catch {
        const { data } = await apolloClient.query<{
          getEventAgreement: EventAgreement | null;
        }>({
          query: GET_EVENT_AGREEMENT,
          variables: { eventId, organizationId },
          fetchPolicy: "network-only",
        });
        agreement = data?.getEventAgreement ?? null;
      }

      const agreementOutcome = getAgreementOutcome(agreement);
      if (agreementOutcome.action !== "complete") {
        throw new Error(agreementOutcome.message);
      }

      clearPendingEvent();
      navigate("/event-submitted", { replace: true });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Unable to create your event. Please try again.";
      showError("Event submission incomplete", errorMessage, 0);
    } finally {
      setSubmissionPhase("idle");
    }
  }, [
    agreementPreview,
    apolloClient,
    canSubmit,
    contractId,
    createEventMutation,
    dismissAll,
    formValues,
    initializeEventAirContractsMutation,
    navigate,
    organizationId,
    showError,
    signatureName,
    signEventAgreementMutation,
    upsertEventLandingPageMutation,
    user?.email,
    user?.id,
    user?.name,
  ]);

  return { submissionPhase, submitAgreement };
}
